import { z, ZodTypeAny } from 'zod';
import type { Step } from '@/components/common';
import type { FieldSchema } from '@/hooks/useInsuranceTypeSchema';
import { groupFieldsBySection, widgetForDataType } from '@/pages/policies/dynamicSchema';

/**
 * The generic, schema-driven quote flow.
 *
 * Motor, Life and Health each own a hand-written zod schema + step list in
 * `schema.ts`. Every OTHER insurance type (travel, home, marine, fire, pet,
 * whatever a Country Admin adds next) is driven from here instead: the step
 * list, the validation and the payload are all derived at runtime from
 * `InsuranceTypeFieldSchema` rows with `applies_to='quote'`, fetched through
 * the same `/products/types/{id}/schema/` endpoint and the same
 * `useInsuranceTypeSchema` hook the Policy create form already uses.
 *
 * Motor/Life/Health are NOT routed through here. Motor in particular is live in
 * production and sync-priced via GoDigit; replacing its wizard with a generic
 * one would put that path at risk for no gain, so `QuoteWizardPage` keeps
 * dispatching those three to their existing branches.
 *
 * Everything on the form is a string (same convention as the motor/life/health
 * branches) so a blank optional number stays blank instead of being coerced to
 * a silent 0 on the way through the resolver. Conversion happens once, in
 * `buildGenericQuotePayload`.
 */

export const GENERIC_STEP_INSURANCE_TYPE = 'insurance-type';
export const GENERIC_STEP_PREMIUM_OPTIONS = 'premium-options';
export const GENERIC_STEP_CUSTOMER = 'customer';
export const GENERIC_STEP_DETAILS = 'details';
export const GENERIC_STEP_REVIEW = 'review';
/** A schema section becomes one step, id `section:<section>`. */
export const GENERIC_SECTION_STEP_PREFIX = 'section:';

export const GENERIC_BUSINESS_TYPE_OPTIONS = [
  { value: 'new', label: 'New Business' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'rollover', label: 'Rollover' },
  { value: 'portability', label: 'Portability' },
];

export const GENERIC_YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/**
 * Form keys the flow owns itself, each mapping to a real `QuoteRequest`
 * column. A schema row using one of these keys is dropped from the dynamic
 * set (see `dynamicFields`) so the two never render twice and fight over the
 * same value -- the built-in control wins, because it is the one wired to the
 * column rather than to the JSON bag.
 */
export const GENERIC_RESERVED_KEYS = new Set([
  'customer_id',
  'customer_name',
  'customer_phone',
  'customer_email',
  'customer_dob',
  'customer_gender',
  'business_type',
  'insurance_group',
  'insurance_subgroup',
  'notes',
  // B9 -- the renewed policy's id is a built-in hidden field, never a schema row.
  'previous_policy_id',
]);

/**
 * Schema keys that ALSO have a first-class `QuoteRequest` column. They stay in
 * the dynamic set (the admin decides whether the type has them at all, and
 * what to label them), and `buildGenericQuotePayload` lifts them onto the
 * column in addition to the JSON bag.
 */
const COLUMN_BACKED_KEYS = new Set(['sum_insured', 'policy_term_years']);

const NUMERIC_DATA_TYPES = new Set(['integer', 'decimal', 'money']);

export type GenericQuoteFormData = {
  customer_id: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_dob?: string;
  customer_gender?: string;
  business_type: string;
  insurance_group?: string;
  insurance_subgroup?: string;
  notes?: string;
  /** B9 -- the policy this quote renews; sent as `previous_policy`. */
  previous_policy_id?: string;
} & Record<string, string | undefined>;

export const GENERIC_BASE_DEFAULT_VALUES: Partial<GenericQuoteFormData> = {
  customer_id: '',
  business_type: 'new',
  insurance_group: '',
  insurance_subgroup: '',
  notes: '',
};

/** The schema rows this flow actually renders. */
export function dynamicFields(fields: FieldSchema[]): FieldSchema[] {
  return (fields ?? []).filter((f) => f && f.key && !GENERIC_RESERVED_KEYS.has(f.key));
}

/** `personal_details` -> `Personal Details`. Sections are free text set by an admin. */
export function humanizeSection(section: string): string {
  const cleaned = (section || 'details').replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return 'Details';
  return cleaned
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Sections in render order: lowest `display_order` in the section first, ties
 * broken alphabetically so the order is stable across reloads.
 */
export function orderedSections(fields: FieldSchema[]): string[] {
  const grouped = groupFieldsBySection(dynamicFields(fields));
  return Object.keys(grouped).sort((a, b) => {
    const rank = (s: string) => Math.min(...grouped[s].map((f) => f.display_order ?? 0));
    const diff = rank(a) - rank(b);
    return diff !== 0 ? diff : a.localeCompare(b);
  });
}

export function sectionStepId(section: string): string {
  return `${GENERIC_SECTION_STEP_PREFIX}${section}`;
}

export function sectionFromStepId(stepId: string): string | null {
  return stepId.startsWith(GENERIC_SECTION_STEP_PREFIX)
    ? stepId.slice(GENERIC_SECTION_STEP_PREFIX.length)
    : null;
}

/**
 * Insurance Type -> Premium Options -> Customer -> Quote Details -> one step
 * per schema section -> Review.
 *
 * Premium Options sits immediately after the type because that is where the
 * brief puts it, and because the insurer/product dropdowns on it are scoped by
 * the chosen insurance type -- it cannot render meaningfully before it, and
 * nothing later in the flow depends on it.
 *
 * A type with no `applies_to='quote'` rows yet still gets a working flow: the
 * four fixed steps carry a complete quote request on their own.
 */
export function buildGenericSteps(fields: FieldSchema[]): Step[] {
  const steps: Step[] = [
    { id: GENERIC_STEP_INSURANCE_TYPE, title: 'Insurance Type' },
    { id: GENERIC_STEP_PREMIUM_OPTIONS, title: 'Premium Options' },
    { id: GENERIC_STEP_CUSTOMER, title: 'Customer' },
    { id: GENERIC_STEP_DETAILS, title: 'Quote Details' },
  ];
  for (const section of orderedSections(fields)) {
    steps.push({ id: sectionStepId(section), title: humanizeSection(section) });
  }
  steps.push({ id: GENERIC_STEP_REVIEW, title: 'Review & Submit' });
  return steps;
}

/**
 * Mandatory-field list per step, fed to `trigger(stepFields)` to gate Next.
 * Section steps return EVERY key in the section, not just the required ones,
 * so a value that is present but invalid also blocks. `review` returns the
 * whole flow so Submit stays disabled until all of it validates (same
 * behaviour the Health branch has).
 */
export function genericStepFields(stepId: string | undefined, fields: FieldSchema[]): string[] {
  if (!stepId) return [];
  if (stepId === GENERIC_STEP_CUSTOMER) return ['customer_id'];
  if (stepId === GENERIC_STEP_DETAILS) return ['business_type'];
  if (stepId === GENERIC_STEP_REVIEW) {
    return ['customer_id', 'business_type', ...dynamicFields(fields).map((f) => f.key)];
  }
  const section = sectionFromStepId(stepId);
  if (section !== null) {
    return dynamicFields(fields)
      .filter((f) => f.section === section)
      .map((f) => f.key);
  }
  // insurance-type and premium-options gate nothing: switching type is always
  // allowed, and premium options are optional (a quote request with none is a
  // perfectly good request awaiting pricing).
  return [];
}

function fieldValidation(field: FieldSchema): Record<string, unknown> {
  return (field.validation ?? {}) as Record<string, unknown>;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * A zod object over the fixed keys plus one optional string per schema field,
 * with the per-field rules applied in a single `superRefine`.
 *
 * The rules live in the refine rather than on each key so an EMPTY optional
 * value short-circuits before any numeric/regex check runs -- the thing
 * `z.coerce.number()` gets wrong (it reads `''` as 0).
 */
export function buildGenericQuoteSchema(fields: FieldSchema[]) {
  const shape: Record<string, ZodTypeAny> = {
    customer_id: z.string().min(1, 'Select or add a customer to continue'),
    customer_name: z.string().optional(),
    customer_phone: z.string().optional(),
    customer_email: z.string().optional(),
    customer_dob: z.string().optional(),
    customer_gender: z.string().optional(),
    business_type: z.string().min(1, 'Business type is required'),
    insurance_group: z.string().optional(),
    insurance_subgroup: z.string().optional(),
    notes: z.string().optional(),
    previous_policy_id: z.string().uuid().optional().or(z.literal('')),
  };
  const dynamic = dynamicFields(fields);
  for (const field of dynamic) shape[field.key] = z.string().optional();

  return z.object(shape).superRefine((data, ctx) => {
    for (const field of dynamic) {
      const raw = String((data as Record<string, unknown>)[field.key] ?? '').trim();
      const add = (message: string) =>
        ctx.addIssue({ path: [field.key], code: z.ZodIssueCode.custom, message });

      if (!raw) {
        if (field.is_required) add(`${field.label} is required`);
        continue;
      }

      const rules = fieldValidation(field);

      if (NUMERIC_DATA_TYPES.has(field.data_type)) {
        const value = Number(raw);
        if (!Number.isFinite(value)) {
          add(`${field.label} must be a number`);
          continue;
        }
        if (field.data_type === 'integer' && !Number.isInteger(value)) {
          add(`${field.label} must be a whole number`);
          continue;
        }
        const min = num(rules.min);
        const max = num(rules.max);
        if (min !== undefined && value < min) add(`${field.label} must be at least ${min}`);
        if (max !== undefined && value > max) add(`${field.label} must be at most ${max}`);
        continue;
      }

      if (field.data_type === 'choice') {
        const choices = field.choices ?? [];
        if (choices.length && !choices.some((c) => c.value === raw)) {
          add(`Choose one of the listed options for ${field.label}`);
        }
        continue;
      }

      if (field.data_type === 'boolean') {
        if (raw !== 'yes' && raw !== 'no') add(`Choose Yes or No for ${field.label}`);
        continue;
      }

      if (typeof rules.regex === 'string' && rules.regex) {
        // The pattern comes from admin-entered data, so a malformed one must
        // not blow up the whole form -- treat it as "no pattern".
        try {
          if (!new RegExp(rules.regex).test(raw)) {
            add(String(rules.message || `${field.label} is not in the expected format`));
          }
        } catch {
          /* ignore an unparsable admin-supplied pattern */
        }
      }

      const maxLength = num(rules.max_length) ?? num(rules.maxLength);
      if (maxLength !== undefined && raw.length > maxLength) {
        add(`${field.label} must be ${maxLength} characters or fewer`);
      }
    }
  });
}

export function genericDefaultValues(fields: FieldSchema[]): Record<string, string> {
  const defaults: Record<string, string> = { ...(GENERIC_BASE_DEFAULT_VALUES as Record<string, string>) };
  for (const field of dynamicFields(fields)) defaults[field.key] = '';
  return defaults;
}

/**
 * Widget to render for a schema field. Delegates to the Policy form's
 * `widgetForDataType` so the two forms cannot drift, then narrows the one case
 * where this flow differs: a boolean renders as a Yes/No select rather than a
 * checkbox, because everything here is a string and a checkbox would put a
 * genuine boolean into an otherwise all-string form.
 */
export function genericWidgetFor(field: FieldSchema): string {
  const widget = widgetForDataType(field);
  return widget === 'checkbox' ? 'yes-no' : widget;
}

const ALLOWED_GENDERS = new Set(['male', 'female', 'other']);

export interface GenericQuotePayloadArgs {
  /** InsuranceType code (the create serializer resolves code, name or UUID). */
  insuranceTypeCode: string;
  data: GenericQuoteFormData;
  fields: FieldSchema[];
  proposerName: string;
  proposerPhone: string;
  proposerEmail?: string;
  proposerDob?: string;
  proposerGender?: string;
}

/**
 * Body for `POST /quotes/requests/` (`QuoteRequestCreateSerializer`).
 *
 * Schema field values go into `additional_data.quote_data` -- the JSON bag the
 * serializer already exposes -- and the two that have real columns
 * (`sum_insured`, `policy_term_years`) are lifted onto those columns as well,
 * so reports that read the columns still see them.
 *
 * `customer_profile_id` is recorded in `additional_data` rather than sent as a
 * field: the create serializer's `customer` FK points at `users.User`, and a
 * customer created inline on the Customer step usually has no login at all.
 * See the note in the agent report -- linking `customer_profile` needs a
 * backend change this task does not own.
 */
export function buildGenericQuotePayload(args: GenericQuotePayloadArgs): Record<string, unknown> {
  const { data, fields } = args;
  const quoteData: Record<string, unknown> = {};
  const payload: Record<string, unknown> = {
    insurance_type: args.insuranceTypeCode,
    business_type: data.business_type || 'new',
    proposer_type: 'individual',
    proposer_name: args.proposerName,
    proposer_phone: args.proposerPhone,
    source: 'agent',
  };

  if (args.proposerEmail) payload.proposer_email = args.proposerEmail;
  if (args.proposerDob) payload.proposer_dob = args.proposerDob;
  if (args.proposerGender && ALLOWED_GENDERS.has(args.proposerGender)) {
    payload.proposer_gender = args.proposerGender;
  }
  if (data.insurance_group) payload.insurance_group = data.insurance_group;
  if (data.insurance_subgroup) payload.insurance_subgroup = data.insurance_subgroup;
  if (data.notes && data.notes.trim()) payload.notes = data.notes.trim();
  // B9: `POST /quotes/requests/` names the renewed policy `previous_policy`
  // (the integrations submit calls it `previous_policy_id`). The server scopes
  // it to the caller's broker and refuses anything else with a `code`.
  if (data.previous_policy_id) payload.previous_policy = data.previous_policy_id;

  for (const field of dynamicFields(fields)) {
    const raw = String(data[field.key] ?? '').trim();
    if (!raw) continue;

    let value: unknown = raw;
    if (NUMERIC_DATA_TYPES.has(field.data_type)) {
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) continue;
      value = parsed;
    } else if (field.data_type === 'boolean') {
      value = raw === 'yes';
    }
    quoteData[field.key] = value;

    if (COLUMN_BACKED_KEYS.has(field.key) && typeof value === 'number') {
      payload[field.key] = value;
    }
  }

  payload.additional_data = {
    insurance_type_code: args.insuranceTypeCode,
    quote_data: quoteData,
    ...(data.customer_id ? { customer_profile_id: data.customer_id } : {}),
  };

  return payload;
}
